import type { SessionChannel, SessionMessage } from './broadcast.ts';

/**
 * Client idle timer (roadmap 3.4). It agrees with the server by construction: the server extends a
 * session only when it records activity, and this timer moves its deadline only when the server
 * confirmed a keep-alive, to (time the keep-alive was sent) + (the session's idle limit). The request
 * reaches the server after it was sent, so the local deadline is never later than the server's.
 *
 *   active ──(deadline − 2:00)──► warning ──(deadline)──► expired      (terminal)
 *   warning ──"Stay signed in" (keep-alive confirmed)──► active
 *   any ── another tab recorded activity (BroadcastChannel) ──► active with that tab's deadline
 *
 * Activity events (pointer, key, touch, becoming visible) are throttled to one keep-alive per 30 s in
 * shared mode (60 s otherwise). While the warning shows, only its buttons count: a stray touch on a lab
 * computer must not silently keep someone else's session open.
 */
export type IdlePhase = 'active' | 'warning' | 'expired';

export const IDLE_WARNING_MS = 2 * 60_000;

export type IdleOptions = {
  timeoutMs: number;
  /** Local time at which the session idles out, computed from the server's view at load. */
  initialDeadline: number;
  warnBeforeMs?: number;
  recordEveryMs: number;
  keepAlive: () => Promise<unknown>;
  onPhase: (phase: IdlePhase, deadline: number) => void;
  channel?: SessionChannel;
  now?: () => number;
};

export class IdleController {
  readonly #o: Required<Omit<IdleOptions, 'channel'>> & { channel: SessionChannel | undefined };
  #deadline: number;
  #lastRecorded = -Infinity;
  #phase: IdlePhase = 'active';
  #timer: ReturnType<typeof setTimeout> | undefined;
  #unsubscribe: (() => void) | undefined;

  constructor(o: IdleOptions) {
    this.#o = { warnBeforeMs: IDLE_WARNING_MS, now: () => Date.now(), channel: undefined, ...o };
    this.#deadline = o.initialDeadline;
  }

  get phase(): IdlePhase {
    return this.#phase;
  }

  get deadline(): number {
    return this.#deadline;
  }

  start(): void {
    this.#unsubscribe = this.#o.channel?.subscribe((m) => this.receive(m));
    this.check();
  }

  stop(): void {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#unsubscribe?.();
  }

  /** A user activity event. Throttled; ignored during the warning and after expiry. */
  activity(): void {
    if (this.#phase !== 'active') return;
    this.check();
    if (this.#phase !== 'active') return;
    const t = this.#o.now();
    if (t - this.#lastRecorded < this.#o.recordEveryMs) return;
    void this.#record(t);
  }

  /** The warning dialog's "Stay signed in". Resolves true when the server confirmed it in time. */
  async stay(): Promise<boolean> {
    if (this.#phase === 'expired') return false;
    return this.#record(this.#o.now());
  }

  async #record(sentAt: number): Promise<boolean> {
    this.#lastRecorded = sentAt;
    try {
      await this.#o.keepAlive();
    } catch {
      return false;   // not confirmed: the deadline stays where the server has it
    }
    if (this.#phase === 'expired') return false;
    const deadline = sentAt + this.#o.timeoutMs;
    this.#o.channel?.post({ type: 'activity', deadline, recordedAt: sentAt });
    this.adopt(deadline);
    return true;
  }

  /** A later deadline (from a confirmed keep-alive here or in another tab) moves the timer. */
  adopt(deadline: number): void {
    if (this.#phase === 'expired' || deadline <= this.#deadline) return;
    this.#deadline = deadline;
    this.check();
  }

  receive(m: SessionMessage): void {
    if (m.type === 'activity') {
      this.#lastRecorded = Math.max(this.#lastRecorded, m.recordedAt);
      this.adopt(m.deadline);
    }
  }

  /** Re-evaluates the phase now (also after the device slept, when timers ran late) and re-arms. */
  check(): void {
    if (this.#phase === 'expired') return;
    const t = this.#o.now();
    const next: IdlePhase = t >= this.#deadline ? 'expired' : t >= this.#deadline - this.#o.warnBeforeMs ? 'warning' : 'active';
    if (next !== this.#phase) {
      this.#phase = next;
      this.#o.onPhase(next, this.#deadline);
    }
    clearTimeout(this.#timer);
    if (next === 'expired') return;
    const at = next === 'active' ? this.#deadline - this.#o.warnBeforeMs : this.#deadline;
    this.#timer = setTimeout(() => this.check(), Math.max(0, at - t));
  }
}
